// Create a ProductMixin

export const ProductMixins = {
    methods: {
        BaseImage(product: any) {
            const url = import.meta.env.VITE_SERVERAPI_URL + '/storage/uploads/products';
            if (product?.base_image?.length > 0) {
                return `${url}/base-img/${product.base_image}`;
            } else {
                return `${url}/base-img/_placeholder_.png`;
            }
        },
        ProductImage(product: any) {
            const url = import.meta.env.VITE_SERVERAPI_URL + '/storage/uploads/products';
            if (product?.product_image != null && product.product_image.length > 0) {
                return `${url}/product-img/${product.product_image}`;
            } else {
                return `${url}/product-img/_placeholder_.png`;
            }
        }
    }
}

export function FormatProductSpecsSummary(p: any): string {
    if (!p) return ''

    const cat = String(p.printer_category || '').trim().toLowerCase()

    if (cat === 'water dispenser' || cat.includes('water dispenser')) {
        const ff = String(p.form_factor || '').trim()
        const cap = String(p.capability || '').trim()
        const clean_ff = (ff === '-' || ff.toLowerCase() === 'n/a' || ff.toLowerCase() === 'none') ? '' : ff
        const clean_cap = (cap === '-' || cap.toLowerCase() === 'n/a' || cap.toLowerCase() === 'none') ? '' : cap
        const parts = [clean_ff, clean_cap].filter(Boolean)
        return parts.join(' • ')
    }

    if (cat === 'shredder' || cat.includes('shredder')) {
        const ff = String(p.form_factor || '').trim()
        const speed = String(p.speed_black || '').trim()
        const clean_ff = (ff === '-' || ff.toLowerCase() === 'n/a' || ff.toLowerCase() === 'none') ? '' : ff
        const speed_clean = speed !== '0' && speed.toLowerCase() !== 'n/a' && speed !== '-' && speed !== '' ? speed : ''
        const parts = [clean_ff, speed_clean].filter(Boolean)
        return parts.join(' • ')
    }

    if (cat === 'ip phone' || cat.includes('phone')) {
        const ff = String(p.form_factor || '').trim()
        if (ff === '' || ff === '-' || ff.toLowerCase() === 'n/a' || ff.toLowerCase() === 'none') {
            return ''
        }
        return ff
    }

    // 1. Color (Color or B&W)
    let color = ''
    const raw_color = String(p.copier_color || '').trim().toLowerCase()
    if (raw_color === 'b&w' || raw_color.includes('mono') || raw_color.includes('black')) {
        color = 'B&W'
    } else if (raw_color === 'color') {
        color = 'Color'
    } else if (p.copier_color) {
        color = String(p.copier_color)
    }

    // 2. Format (A3 or A4)
    const ff = String(p.form_factor || '').toLowerCase()
    const paper_raw = typeof p.paper_size === 'string'
        ? p.paper_size.toLowerCase()
        : JSON.stringify(p.paper_size || '').toLowerCase()
    const is_a3_detected = paper_raw.includes('a3') || paper_raw.includes('tabloid') || paper_raw.includes('11x17')
    const format = is_a3_detected ? 'A3' : 'A4'

    // 3. Speed (e.g. 50 ppm)
    let speed = ''
    const raw_speed = Number(p.speed_black || p.speed_color || p.copy_speed || 0)
    if (raw_speed > 0) {
        speed = `${raw_speed} ppm`
    }

    // 4. Desktop / Floor
    let placement = ''
    if (ff.includes('desktop') || ff.includes('portable')) {
        placement = 'Desktop'
    } else if (ff.includes('floor') || ff.includes('freestanding')) {
        placement = 'Floor'
    } else if (is_a3_detected) {
        placement = 'Floor'
    } else {
        placement = 'Desktop'
    }

    const parts = [color, format, speed, placement].filter(Boolean)
    return parts.join(' • ')
}

export function MatchProductPrintVolume(product: any, volumes: string[]): boolean {
    if (!volumes || volumes.length === 0) return true

    let monthly_vol = 0
    const raw_vol = String(product.avg_monthly_prints || '').replace(/,/g, '').trim()
    if (raw_vol && !isNaN(Number(raw_vol)) && Number(raw_vol) > 0) {
        monthly_vol = Number(raw_vol)
    } else {
        const speed = Number(product.speed_black || product.speed_color || 0)
        if (speed > 50) {
            monthly_vol = 15000
        } else if (speed >= 30) {
            monthly_vol = 5000
        } else if (speed > 0) {
            monthly_vol = 2000
        } else {
            const usage = String(product.usage_class || '').toLowerCase()
            if (usage.includes('production') || usage.includes('enterprise')) {
                monthly_vol = 25000
            } else if (usage.includes('small') || usage.includes('medium')) {
                monthly_vol = 5000
            } else {
                monthly_vol = 1500
            }
        }
    }

    return volumes.some(vol => {
        if (vol === '<3000') {
            return monthly_vol < 3000
        } else if (vol === '3000-10000') {
            return monthly_vol >= 3000 && monthly_vol <= 10000
        } else if (vol === '10000+') {
            return monthly_vol > 10000
        }
        return false
    })
}

export function MatchProductOfficeSize(product: any, sizes: string[]): boolean {
    if (!sizes || sizes.length === 0) return true

    const speed = Number(product.speed_black || product.speed_color || 0)
    const usage = String(product.usage_class || '').toLowerCase()
    const form_factor = String(product.form_factor || '').toLowerCase()
    const raw_vol = Number(String(product.avg_monthly_prints || '').replace(/,/g, '').trim()) || 0

    return sizes.some(size => {
        if (size === '1-10') {
            return (
                usage.includes('personal') ||
                form_factor.includes('desktop') ||
                form_factor.includes('portable') ||
                (speed > 0 && speed <= 35) ||
                (raw_vol > 0 && raw_vol < 3000)
            )
        } else if (size === '10-25') {
            return (
                usage.includes('small office') ||
                form_factor.includes('workgroup') ||
                (speed >= 25 && speed <= 50) ||
                (raw_vol >= 3000 && raw_vol <= 10000)
            )
        } else if (size === '25-50') {
            return (
                (speed >= 40 && speed <= 70) ||
                (raw_vol >= 10000 && raw_vol <= 30000) ||
                (usage.includes('production') && speed <= 70)
            )
        } else if (size === '50+') {
            return (
                usage.includes('enterprise') ||
                (usage.includes('production') && (speed >= 55 || raw_vol >= 25000)) ||
                speed >= 60 ||
                raw_vol > 30000
            )
        }
        return false
    })
}
